/**
 * DocumentoFolhas — folhas A4 da Lista de Compras em React (resumo + 1 por
 * ambiente). Identidade NEUTRA do ERP. Usa markup de classes simples + CSS
 * injetado (fonte única `documentoCss`, escopada em #rf-print-papers), o que
 * permite reusar exatamente o mesmo visual no preview, no "Baixar PDF"
 * (html2canvas) e na janela de "Imprimir".
 *
 * `ambientes` já normalizados (ver lib/documento.js).
 */
import React from "react";

import { buildSummary, groupByModule } from "./lib/parseProjeto";
import { acentoDoApp, documentoCss } from "./lib/documentoCss";
import { fmtQt, moduloSemCodigo } from "./lib/documento";

function hojeBR() {
  return new Date().toLocaleDateString("pt-BR");
}

function TabelaItens({ items, comNumero }) {
  let n = 0;
  return (
    <table className="itens">
      <thead>
        <tr>
          <th className="c-num">#</th>
          <th className="c-qt">Qt</th>
          <th>Descrição</th>
          <th className="c-dim">Dimensões</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => {
          n += 1;
          const meta = [it.manufacturer, it.description].filter(Boolean).join(" · ");
          return (
            <tr key={it.id || n}>
              <td className="c-num">{comNumero ? String(n).padStart(3, "0") : ""}</td>
              <td className="c-qt">{fmtQt(it.qt)}</td>
              <td>
                <div className="i-nome">{it.name || "—"}</div>
                {meta ? <span className="i-meta">{meta}</span> : null}
              </td>
              <td className="c-dim">{it.dimensions || "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Cabecalho() {
  return (
    <header className="phead">
      <div className="phead-doc">Lista de Compras</div>
      <div className="phead-date">{hojeBR()}</div>
    </header>
  );
}

function Rodape({ pagina, total }) {
  return (
    <footer className="pfoot">
      <span>Lista de Compras</span>
      <span>Folha {pagina} / {total}</span>
    </footer>
  );
}

/** Valores distintos de um campo entre os ambientes (junta com " / "), ou "—". */
function distintos(ambientes, campo) {
  const vals = [...new Set(ambientes.map((a) => a[campo]).filter(Boolean))];
  return vals.length ? vals.join(" / ") : "—";
}

function FolhaResumo({ ambientes, pagina, total }) {
  const itens = buildSummary(ambientes);
  const totalUnidades = itens.reduce((s, i) => s + i.qt, 0);
  const ambNomes = ambientes.map((a) => `#${a.projectId} ${a.nome || "Ambiente"}`).join("  /  ");
  return (
    <article className="rf-paper">
      <Cabecalho />
      <div className="meta">
        <span><b>Cliente:</b> {distintos(ambientes, "cliente")}</span>
        <span><b>Autor:</b> {distintos(ambientes, "autor")}</span>
        <span><b>Status:</b> {distintos(ambientes, "status")}</span>
      </div>
      <div className="banner">
        <span className="banner-nome">{ambNomes}</span>
        <span className="banner-folha">Folha {pagina} de {total}</span>
      </div>
      {itens.length === 0 ? (
        <div className="vazio">Nenhum item para resumir.</div>
      ) : (
        <>
          <div className="resumo-intro">
            {itens.length} {itens.length === 1 ? "item distinto" : "itens distintos"} ·{" "}
            {fmtQt(totalUnidades)} unidades no total
          </div>
          <TabelaItens items={itens} comNumero={false} />
        </>
      )}
      <Rodape pagina={pagina} total={total} />
    </article>
  );
}

function FolhaAmbiente({ amb, pagina, total }) {
  const visiveis = (amb.items || []).filter((i) => !i.hidden && Number(i.qt) > 0);
  const grupos = groupByModule(visiveis);
  return (
    <article className="rf-paper">
      <Cabecalho />
      <div className="meta">
        <span><b>Cliente:</b> {amb.cliente || "—"}</span>
        <span><b>Projeto:</b> #{amb.projectId}</span>
        <span><b>Autor:</b> {amb.autor || "—"}</span>
        <span><b>Status:</b> {amb.status || "—"}</span>
      </div>
      <div className="banner">
        <span className="banner-id">#{amb.projectId}</span>
        <span className="banner-nome">{amb.nome || "Ambiente"}</span>
        <span className="banner-folha">Folha {pagina} de {total}</span>
      </div>
      {visiveis.length === 0 ? (
        <div className="vazio">Nenhum item para listar neste ambiente.</div>
      ) : (
        grupos.map((g) => (
            <div key={g.moduleId}>
              <div className="modulo">
                {!moduloSemCodigo(g.moduleId) ? <span className="mod-mid">{g.moduleId}</span> : null}
                <span className="mod-nome">{g.moduleName}</span>
                <span className="mod-count">
                  {g.items.length} {g.items.length === 1 ? "item" : "itens"}
                </span>
              </div>
              <TabelaItens items={g.items} comNumero />
            </div>
          ))
      )}
      <Rodape pagina={pagina} total={total} />
    </article>
  );
}

export default function DocumentoFolhas({ ambientes }) {
  // Acento lido a cada render (não memoizado) — sempre reflete o tema atual.
  const css = documentoCss(acentoDoApp());
  const lista = ambientes || [];
  const total = lista.length + 1; // +1 resumo
  return (
    <div id="rf-print-papers">
      {/* eslint-disable-next-line react/no-danger */}
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <FolhaResumo ambientes={lista} pagina={1} total={total} />
      {lista.map((amb, idx) => (
        <FolhaAmbiente key={amb.projectId} amb={amb} pagina={idx + 2} total={total} />
      ))}
    </div>
  );
}
